import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import LocationPermission from "../../components/common/LocationPermission";
import { logout } from "../../features/auth/authSlice";
import { selectAuthState } from "../../features/auth/authSelectors";

import { setLanguage } from "../../features/language/languageSlice";
import {selectLanguage} from "../../features/language/languageSelector";

import { useTranslation } from "../../hooks/useTranslations";

const HIDDEN_USER_FIELDS = ["access_token", "token", "refresh_token", "password", "id"];

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};


function SettingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(selectAuthState) || {};

const { t } = useTranslation();

  const userDetails = Object.entries(user || {}).filter(
    ([key]) => !HIDDEN_USER_FIELDS.includes(key.toLowerCase())
  );

  const displayName = user?.username || user?.name || user?.email || "User";
  const initials = displayName.charAt(0).toUpperCase();

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const language = useSelector(selectLanguage);
  console.log(language); // "en" or "bn"

  return (
    <div className="min-h-screen bg-background text-text font-sans pb-12">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <section className="bg-surface rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-border">
            <h2 className="text-xl font-bold text-text">{t("settings.title")}</h2>
            <p className="text-sm text-text-muted mt-1">{t("settings.subtitle")}</p>
          </div>

          <div className="p-5 sm:p-6 space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shrink-0">
                <span className="text-primary font-extrabold text-2xl">{initials}</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-text">{displayName}</h3>
                <p className="text-sm text-text-muted">{t("settings.signedInAccount")}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {userDetails.length > 0 ? (
                userDetails.map(([key, value]) => (
                  <div key={key} className="rounded-xl border border-border bg-background p-4">
                    <p className="text-xs font-bold text-text-muted uppercase tracking-wider">
                      {formatLabel(key)}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-text break-words">
                      {formatValue(value)}
                    </p>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-border bg-background p-4 sm:col-span-2">
                  <p className="text-sm font-medium text-text-muted">
                    No user details are available for this account.
                  </p>
                </div>
              )}
            </div>

            <div className="pt-6 border-t border-border">
              <h3 className="text-lg font-bold text-text mb-4">
                Survey Language
              </h3>

              <div className="rounded-xl border border-border bg-background p-4">
                <label className="block text-sm font-semibold text-text mb-2">
                  Select Language
                </label>

                <select
                  value={language}
                  onChange={(e) =>
                    dispatch(setLanguage(e.target.value))
                  }
                  className="w-full sm:w-64 rounded-xl border border-border bg-surface px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="en">
                    English
                  </option>
                
                  <option value="bn">
                    বাংলা
                  </option>

                  <option value="hi">
                    हिंदी
                  </option>
                </select>
                
                <p className="text-xs text-text-muted mt-2">
                  Survey questions will appear in the selected language.
                </p>
              </div>
            </div>

            <div className="pt-6 border-t border-border">
              <button
                onClick={handleLogout}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-red-50 border border-red-100 text-error font-semibold text-sm hover:bg-red-100 transition-all active:scale-95"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </section>

        <LocationPermission />
      </main>
    </div>
  );
}

export default SettingsPage;
